import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, LogOut, Menu, MonitorSmartphone, PanelLeftClose, PanelLeftOpen, Search, Settings2, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import GlobalSearch from "@/components/lightning/GlobalSearch";
import ReminderCenter from "@/components/lightning/ReminderCenter";
import TeamPanel from "@/components/team/TeamPanel";
import OrgSwitcher from "@/components/OrgSwitcher";
import { CompanyLogo } from "@/components/BrandLogo";
import ChangePasswordDialog from "@/components/auth/ChangePasswordDialog";
import DevicesDialog from "@/components/auth/DevicesDialog";

/** Global top bar: sidebar toggle, brand, search (Ctrl/⌘ K), reminders and the account menu. Page titles and actions belong to
 * each page's own header (components/Topbar.jsx). */
export default function ModernTopbar({ collapsed, onToggle, onOpenMobile }) {
  const { user, tenant, logout } = useAuth();
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  const [changePw, setChangePw] = useState(false);
  const [devices, setDevices] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setMenu(false); };
    const onKey = (e) => { if (e.key === "Escape") setMenu(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, []);

  const initials = (user?.name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const iconBtn = "inline-flex items-center justify-center w-10 h-10 rounded-[var(--radius-md)] text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)] focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]";

  return (
    <header className="sticky top-0 z-40 h-14 shrink-0 flex items-center gap-2 px-3 sm:px-4 bg-[var(--color-surface)] border-b border-[var(--color-border)]" data-testid="modern-topbar">
      <button type="button" onClick={onOpenMobile} className={`lg:hidden ${iconBtn}`} aria-label="Open navigation" data-testid="modern-open-nav"><Menu size={19} /></button>
      <button type="button" onClick={onToggle} className={`hidden lg:inline-flex ${iconBtn}`} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} aria-pressed={collapsed}
              data-testid="modern-collapse">
        {collapsed ? <PanelLeftOpen size={19} /> : <PanelLeftClose size={19} />}
      </button>
      {/* The company's logo on every screen (MADIO's wordmark for MADIO; a letter mark for a company without one). */}
      <Link to="/" className="flex items-center gap-2 shrink-0 rounded focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]" aria-label={`${tenant?.name || "MADIO"} home`}>
        <CompanyLogo className="h-[18px] sm:h-[22px]" showName nameClass="hidden lg:block font-heading font-bold text-[15px] tracking-tight text-[var(--color-navy)]" />
      </Link>
      <OrgSwitcher />
      <div className="flex-1 hidden sm:flex justify-center min-w-0 px-2"><GlobalSearch /></div>
      <div className="flex-1 sm:hidden" />
      <button type="button" className={`sm:hidden ${iconBtn}`} aria-label="Search" onClick={() => setSearch(true)} data-testid="mobile-search-btn"><Search size={19} /></button>
      <TeamPanel />
      <ReminderCenter />
      <div className="relative" ref={ref}>
        <button type="button" onClick={() => setMenu((v) => !v)} aria-expanded={menu} aria-haspopup="menu" aria-label={`Account menu for ${user?.name || "you"}`}
                className="w-9 h-9 rounded-full bg-[var(--color-primary-soft)] text-[var(--color-primary-hover)] font-bold text-xs grid place-items-center focus-visible:outline-2 focus-visible:outline-[var(--color-primary)]" data-testid="modern-profile">
          {initials}
        </button>
        {menu && (
          <div role="menu" className="absolute right-0 mt-2 w-60 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-[var(--shadow-pop)] z-50 py-1">
            <div className="px-4 py-3 border-b border-[var(--color-border)]">
              <div className="font-semibold text-sm text-[var(--color-text)]">{user?.name}</div>
              <div className="text-xs text-[var(--color-text-muted)]">{tenant?.name}</div>
            </div>
            {user?.role === "admin" && (
              <Link role="menuitem" to="/admin/master-data" onClick={() => setMenu(false)} className="flex items-center gap-2 px-4 min-h-[40px] text-sm hover:bg-[var(--color-surface-muted)]"><Settings2 size={15} /> Setup</Link>
            )}
            <button role="menuitem" type="button" onClick={() => { setMenu(false); setChangePw(true); }}
                    className="w-full flex items-center gap-2 px-4 min-h-[40px] text-sm hover:bg-[var(--color-surface-muted)]" data-testid="change-password-btn"><KeyRound size={15} /> Change password</button>
            <button role="menuitem" type="button" onClick={() => { setMenu(false); setDevices(true); }}
                    className="w-full flex items-center gap-2 px-4 min-h-[40px] text-sm hover:bg-[var(--color-surface-muted)]" data-testid="devices-btn"><MonitorSmartphone size={15} /> Signed-in devices</button>
            <button role="menuitem" type="button" onClick={logout} className="w-full flex items-center gap-2 px-4 min-h-[40px] text-sm hover:bg-[var(--color-surface-muted)]" data-testid="light-logout"><LogOut size={15} /> Log out</button>
          </div>
        )}
      </div>
      {changePw && <ChangePasswordDialog onClose={() => setChangePw(false)} />}
      {devices && <DevicesDialog onClose={() => setDevices(false)} />}
      {search && (
        <div className="sm:hidden absolute inset-x-0 top-0 z-50 bg-[var(--color-surface)] p-3 flex gap-2 shadow-lg border-b border-[var(--color-border)]">
          <div className="flex-1"><GlobalSearch /></div>
          <button type="button" className={iconBtn} aria-label="Close search" onClick={() => setSearch(false)}><X size={19} /></button>
        </div>
      )}
    </header>
  );
}
